import { get, set, update } from 'idb-keyval';
import { useSyncStore } from '@/stores/useSyncStore';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import type { Service } from '@/shared/model';
import { SERVICES_DB } from '@/entities/service/data/initialData';
import { parseSafeNumber } from '@/shared/lib/number';
import { z } from 'zod';

async function readFromIdb<T>(key: string): Promise<T | undefined> {
  try {
    return await get<T>(key);
  } catch {
    return undefined;
  }
}

function readFromLocalStorage<T>(key: string): T | undefined {
  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : undefined;
  } catch {
    return undefined;
  }
}

export async function migrateIdbToZustand(): Promise<void> {
  try {
    const idbAvailable = await (async () => {
      try {
        await get('__idb_test__');
        return true;
      } catch {
        return false;
      }
    })();

    const rooms = idbAvailable ? await readFromIdb<any>('bg2_rooms') : undefined;
    const meta = idbAvailable ? await readFromIdb<any>('bg2_project_meta') : undefined;
    const activeProjectId = idbAvailable ? await readFromIdb<string | number | null>('bg2_active_project_id') : undefined;
    const safetyMargin = idbAvailable ? await readFromIdb<number>('bg2_safetyMargin') : undefined;
    const activeRoomId = idbAvailable ? await readFromIdb<string | null>('bg2_activeRoomId') : undefined;

    if (rooms !== undefined || meta !== undefined || activeProjectId !== undefined) {
      const projectExisting = window.localStorage.getItem('estimatix-project');
      if (!projectExisting) {
        const projectData = {
          state: {
            rooms: rooms ?? readFromLocalStorage('bg2_rooms') ?? [],
            meta: meta ?? readFromLocalStorage('bg2_project_meta') ?? {},
            activeProjectId: activeProjectId ?? readFromLocalStorage('bg2_active_project_id') ?? null,
            safetyMargin: safetyMargin ?? readFromLocalStorage('bg2_safetyMargin') ?? 0,
            activeRoomId: activeRoomId ?? readFromLocalStorage('bg2_activeRoomId') ?? null,
          },
          version: 0,
        };
        window.localStorage.setItem('estimatix-project', JSON.stringify(projectData));
      }
    }

    const services = idbAvailable ? await readFromIdb<any>('bg2_services') : undefined;
    const categories = idbAvailable ? await readFromIdb<any>('bg2_categories') : undefined;
    const units = idbAvailable ? await readFromIdb<any>('bg2_units') : undefined;

    if (services !== undefined || categories !== undefined) {
      const serviceExisting = window.localStorage.getItem('estimatix-services');
      if (!serviceExisting) {
        const serviceData = {
          state: {
            rawServices: services ?? readFromLocalStorage('bg2_services'),
            rawCategories: categories ?? readFromLocalStorage('bg2_categories'),
            units: units ?? readFromLocalStorage('bg2_units'),
          },
          version: 0,
        };
        window.localStorage.setItem('estimatix-services', JSON.stringify(serviceData));
      }
    }

    const recipes = idbAvailable ? await readFromIdb<any>('bg2_recipes') : undefined;
    if (recipes !== undefined) {
      const recipeExisting = window.localStorage.getItem('estimatix-recipes');
      if (!recipeExisting) {
        const recipeData = {
          state: { recipes },
          version: 0,
        };
        window.localStorage.setItem('estimatix-recipes', JSON.stringify(recipeData));
      }
    }

    const currency = idbAvailable ? await readFromIdb<string>('bg2_currency') : undefined;
    const theme = readFromLocalStorage<string>('theme');
    if (currency !== undefined || theme !== undefined) {
      const uiExisting = window.localStorage.getItem('estimatix-ui');
      if (!uiExisting) {
        const uiData = {
          state: {
            theme: theme || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
            currency: currency ?? readFromLocalStorage('bg2_currency') ?? 'PLN',
          },
          version: 0,
        };
        window.localStorage.setItem('estimatix-ui', JSON.stringify(uiData));
      }
    }
  } catch (e) {
    console.warn('[migrateIdbToZustand] Migration failed:', e);
  }
}

// ---------------------------------------------------------------------------
// Cloud migration — upload local guest projects to the cloud on first login
//
// Contract (CLOUD-06 / T-04-13, T-04-14):
//
//   - getMigratedFlag(userId) (localStorage key):   'estimatix-cloud-migrated:<userId>'
//     Scoped per userId (HARDEN-01) so a second account on the same browser gets
//     its own independent flag and is not blocked by User A's completed migration.
//     Set ONLY after ALL local projects are confirmed by the server.
//     NEVER set it before.
//
//   - MIGRATED_IDS_KEY (IDB set of string[]):   'estimatix-migrated-project-ids'
//     Tracks confirmed project IDs per-upload so migration is RESUMABLE.
//     Cleared after full success (flag set).
//
//   - Local data (estimatix-project, bg2_library) is NEVER mutated.
//     Local stays the source-of-truth offline cache; cloud is the backup.
//
//   - Boot order: migrateLocalToCloud must only be called AFTER App.tsx's boot chain has
//     resolved useProjectStore.persist.rehydrate() (SAFE-05) so nameIsManual is backfilled
//     (via migrateProjectStore, the store's persist `migrate` option) before upload.
// ---------------------------------------------------------------------------

// HARDEN-01: per-userId flag key so migrations on the same browser are isolated per account.
// Old global key 'estimatix-cloud-migrated' becomes inert; affected users re-run the
// idempotent migration once (LAYER-2 server check guards against duplicate uploads).
const getMigratedFlag = (userId: string) => `estimatix-cloud-migrated:${userId}`;
const MIGRATED_IDS_KEY = 'estimatix-migrated-project-ids';

interface ProjectSnapshot {
  id: string | number;
  name: string;
  rooms?: unknown[];
  [key: string]: unknown;
}

/**
 * Uploads all local guest projects to the authenticated user's cloud account.
 *
 * Uses the upload-confirmed-then-mark contract:
 *   1. Read all local projects (active from localStorage + library from IDB).
 *   2. For each project NOT yet in the confirmed-id set: PUT to cloud.
 *   3. On server confirmation (200 ok): add to the confirmed set (persist immediately).
 *   4. Set the global migrated flag ONLY when confirmedSet.size >= total project count.
 *
 * Returns { migrated, errors } — errors are counted but do NOT abort the loop;
 * un-uploaded projects are left unconfirmed for the next resume run.
 */
export async function migrateLocalToCloud(
  // userId scopes both the LAYER-1 flag key and serves as the HTTP auth identity.
  // The actual HTTP auth uses the session cookie automatically.
  userId: string,
): Promise<{ migrated: number; errors: number; skipped?: boolean; conflicts?: number }> {
  // LAYER 1: Fast-path cache — per-userId localStorage flag (HARDEN-01).
  // Set only after all local projects are confirmed by the server.
  if (localStorage.getItem(getMigratedFlag(userId)) === 'true') {
    return { migrated: 0, errors: 0, skipped: true };
  }

  // LAYER 2: MIG-02 server-check — ask the server whether this account already has projects.
  // A second/third browser must determine migration need from server fact, not the per-browser
  // localStorage flag (which the other browser already set but this browser has never seen).
  try {
    // IN-03 fix: 10 s AbortSignal timeout prevents a hung Worker cold-start from
    // blocking the migration path for the entire session. On timeout the AbortError
    // falls to the catch block below → safe-default: skip migration (same as offline).
    const serverCheckRes = await fetch('/api/projects', {
      method: 'GET',
      signal: AbortSignal.timeout(10_000),
    });
    if (serverCheckRes.ok) {
      const body = (await serverCheckRes.json()) as { projects: { id: string }[] };
      if (body.projects.length > 0) {
        // Server already has data — this account was migrated from another browser.
        // Set the local fast-path flag so subsequent logins skip the GET round-trip.
        localStorage.setItem(getMigratedFlag(userId), 'true');
        return { migrated: 0, errors: 0, skipped: true };
      }
      // Server returned ok with an empty projects array — proceed with migration below.
    } else {
      // Non-ok HTTP (e.g. 401 if session expired mid-request) — safe-default: do not migrate.
      // Never overwrite an unknown server state (T-11-11 mitigation).
      return { migrated: 0, errors: 0, skipped: true };
    }
  } catch {
    // Network error / offline — safe-default: do not migrate.
    // Never attempt PUT when the server state is unknown (Pattern 3: offline-first safety).
    return { migrated: 0, errors: 0, skipped: true };
  }

  // Read library from IDB (bg2_library)
  const library: ProjectSnapshot[] = (await readFromIdb<ProjectSnapshot[]>('bg2_library')) ?? [];

  // Read active project from localStorage Zustand-persisted shape { state: { ... }, version: 0 }
  const projectRaw = (() => {
    try {
      return window.localStorage.getItem('estimatix-project');
    } catch {
      return null;
    }
  })();

  const activeProject: ProjectSnapshot | null = projectRaw
    ? (() => {
        try {
          const parsed = JSON.parse(projectRaw)?.state;
          if (parsed && typeof parsed === 'object' && 'activeProjectId' in parsed) {
            // The Zustand state contains rooms/meta/activeProjectId — reconstruct a project snapshot
            const state = parsed as {
              rooms?: unknown[];
              meta?: Record<string, unknown>;
              activeProjectId?: string | number | null;
              activeRoomId?: string | null;
              safetyMargin?: number;
            };
            if (!state.activeProjectId) return null;
            // CR-03 fix: construct a body that matches ProjectSnapshot shape exactly.
            // Previously missing: lastModified + activeRoomId → server returned 400 on PUT.
            // ProjectSnapshotSchema requires lastModified (string) and activeRoomId (string|null).
            // meta IS allowed by schema but we pass it through for data fidelity.
            return {
              id: state.activeProjectId,
              name: (state.meta as Record<string, unknown> | undefined)?.name as string ?? 'Untitled',
              rooms: state.rooms ?? [],
              lastModified: new Date().toISOString(),
              activeRoomId: state.activeRoomId ?? null,
              safetyMargin: state.safetyMargin ?? 0,
            } satisfies ProjectSnapshot;
          }
          // Fallback: the state might already be a full ProjectSnapshot shape
          if (parsed && typeof parsed.id !== 'undefined') {
            return parsed as ProjectSnapshot;
          }
          return null;
        } catch {
          return null;
        }
      })()
    : null;

  // Deduplicate by project id: active project first, then library entries not already included
  const seen = new Set<string>();
  const allProjects: ProjectSnapshot[] = [];

  if (activeProject) {
    const id = String(activeProject.id);
    seen.add(id);
    allProjects.push(activeProject);
  }

  for (const project of library) {
    const id = String(project.id);
    if (!seen.has(id)) {
      seen.add(id);
      allProjects.push(project);
    }
  }

  // No local data — mark migrated immediately
  if (allProjects.length === 0) {
    localStorage.setItem(getMigratedFlag(userId), 'true');
    return { migrated: 0, errors: 0 };
  }

  // Load confirmed-id set from IDB (resumability)
  const confirmedIds: string[] = (await readFromIdb<string[]>(MIGRATED_IDS_KEY)) ?? [];
  const confirmedSet = new Set<string>(confirmedIds);

  let migratedCount = 0;
  let errorCount = 0;
  let conflictCount = 0;

  for (const project of allProjects) {
    const projectId = String(project.id);
    if (confirmedSet.has(projectId)) continue; // Already confirmed in a previous run

    try {
      // IN-03 fix: per-project 10 s timeout — a hung server cannot block the full
      // migration loop indefinitely. On timeout the AbortError falls to the catch block
      // below → errorCount++ → project left unconfirmed for next resume run (no data loss).
      const res = await fetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          // Session cookie sent automatically — no explicit auth header needed
        },
        signal: AbortSignal.timeout(10_000),
        // Pattern 4: migration uploads are always new-to-server projects, so baseVersion is null.
        // The server treats null as "no prior version → blind upsert allowed".
        body: JSON.stringify({ ...project, baseVersion: null }),
      });

      if (res.ok) {
        confirmedSet.add(projectId);
        // WR-03 fix: use update() (atomic read-modify-write) instead of set() (blind write).
        // Concurrent migration runs (see WR-02) could race on set(): both read the same
        // confirmedIds, write different project subsets, and one overwrites the other's
        // confirmed set. update() is atomic: the IDB engine serialises concurrent updaters.
        await update<string[]>(MIGRATED_IDS_KEY, (existing) => {
          const s = new Set(existing ?? []);
          s.add(projectId);
          return [...s];
        });
        migratedCount++;
      } else if (res.status === 409) {
        // SYNC-02 client / Pitfall 3: race condition — GET returned empty but another parallel
        // login already uploaded this project before our PUT. Server wins (server-authoritative OCC).
        // Do NOT add to confirmedSet (project is on the server but not confirmed by this run).
        // Do NOT increment errorCount (it is not a retryable error — T-11-09 mitigation).
        // Do NOT re-PUT (that would restart a 409 loop — Pitfall 2 mitigation).
        // Continue the loop so remaining projects still upload.
        // Drain the 409 body (serverSnapshot available for future conflict resolution if needed).
        // WR-03 fix: wrap in its own try/catch — a malformed/empty body must not fall to the
        // outer catch which would count this as errorCount instead of conflictCount, and would
        // skip setStatus('conflict'). The 409 semantic is always a conflict regardless of body.
        try {
          await res.json();
        } catch {
          // Body parse failure — ignore; 409 is still a conflict, not an error
        }
        conflictCount++;
        // Surface a non-blocking conflict signal in the sync badge (plan 11-02 presentation layer).
        useSyncStore.getState().setStatus('conflict');
      } else {
        // Non-OK (e.g. 500, 400) — count as error, continue to next project
        errorCount++;
      }
    } catch {
      // Network error — leave this project unconfirmed for next resume run
      errorCount++;
    }
  }

  // ONLY set per-userId migrated flag when ALL projects are confirmed (T-04-13, HARDEN-01)
  if (confirmedSet.size >= allProjects.length) {
    localStorage.setItem(getMigratedFlag(userId), 'true');
    // Clean up per-project tracking (no longer needed)
    await set(MIGRATED_IDS_KEY, null);
    // Phase 21 (ONBOARD-03): sync local onboarding_done to server after migration completes.
    // Fire-and-forget — prevents the checklist re-appearing on a new device after guest→cloud.
    // Sent as a SEPARATE POST (not in the project PUT body) to avoid breaking
    // ProjectUpdateBodySchema Zod validation (Pitfall 2 in 21-RESEARCH.md).
    const { onboardingDone } = useOnboardingStore.getState();
    if (onboardingDone) {
      void fetch('/api/onboarding/complete', { method: 'POST' }).catch(() => {});
    }
  }

  // WR-04 fix: auto-dismiss the 'conflict' badge after migration completes (CONTEXT OQ-3).
  // "Briefly resolved" — show the badge for 3 s so the user can see it, then return to idle.
  // The guard checks current status to avoid interrupting a concurrent flush() that may have
  // already transitioned to 'syncing' or 'synced'.
  //
  // WR-01 fix (36-REVIEW.md iteration 3): dismissing unconditionally to 'idle' silently wiped
  // a rejectedCount/error that a CONCURRENTLY-running flush() may have set earlier in the same
  // login cycle — useAuth.ts fires syncAndHydrate() and migrateLocalToCloud() unawaited from
  // the same effect, so a genuine rejection racing this timer is a normal login-time event, not
  // an edge case (see WR-01's full trace). setStatus() already exempts 'conflict' from resetting
  // rejectedCount/error (WR-05), but that protection only lasts until THIS timer fires — so the
  // timer itself must check for a live rejection signal before dismissing, and hand off to
  // 'error' (which SyncStatusBadge already renders via sync.error_rejected) instead of erasing
  // it. Only dismiss to a clean 'idle' when there is genuinely nothing left to report.
  if (conflictCount > 0 && errorCount === 0) {
    setTimeout(() => {
      const syncState = useSyncStore.getState();
      if (syncState.status === 'conflict') {
        if (syncState.rejectedCount > 0) {
          useSyncStore.setState({ status: 'error' });
        } else {
          syncState.setStatus('idle');
        }
      }
    }, 3000);
  }

  return { migrated: migratedCount, errors: errorCount, conflicts: conflictCount };
}

// ---------------------------------------------------------------------------
// Catalog cloud migration — upload guest catalog customizations on first login
//
// Contract (MIG-01 / MIG-03):
//
//   - getCatalogMigratedFlag(userId) (localStorage key): 'estimatix-catalog-migrated:<userId>'
//     DISTINCT from getMigratedFlag (project migration flag).
//     Scoped per userId (HARDEN-01) so a second account on the same browser gets
//     its own independent flag and is not blocked by User A's completed migration.
//     Set ONLY after ALL uploads succeed (Pitfall 3 / T-13-05).
//     Never set on partial failure — next login retries idempotently.
//
//   - Two-layer guard (mirrors migrateLocalToCloud MIG-02 pattern):
//     LAYER 1: per-userId localStorage flag (fast-path skip).
//     LAYER 2: server GET /api/catalog/overrides (multi-browser / second-browser guard).
//
//   - Local data ('estimatix-services', 'estimatix-project') NEVER mutated.
//     Read-only diff scan; never alters the local Zustand-persisted source.
//
//   - Offline / non-ok server → safe-default: early return, zero writes.
//
//   - NaN local price → clamped to 0 via Math.max(0, parseSafeNumber) (CR-01).
//
// Boot order: call after migrateLocalToCloud (parallel is fine via Promise.all).
// ---------------------------------------------------------------------------

// HARDEN-01: per-userId catalog flag key, mirrors getMigratedFlag pattern above.
const getCatalogMigratedFlag = (userId: string) => `estimatix-catalog-migrated:${userId}`;

/**
 * Uploads guest catalog customizations to the authenticated user's cloud account
 * on first login. Idempotent — safe to retry; upsert by id on the server side.
 *
 * Diff algorithm:
 *   - System services (isCustom falsy): upload if priceLabor OR name differs from SERVICES_DB bundle.
 *   - Custom services (isCustom true): always upload (PUT /api/catalog/services/:id).
 *
 * Returns void. Always resolves; never rejects (fire-and-forget safe).
 */
export async function migrateCatalogToCloud(userId: string): Promise<void> {
  // LAYER 1: per-userId fast-path (HARDEN-01 — unchanged from previous login on same browser for same account)
  if (localStorage.getItem(getCatalogMigratedFlag(userId)) === 'true') return;

  // LAYER 2a: server check — ask server if this account already has catalog overrides.
  // Covers second-browser scenario (MIG-02 / Pitfall 4): Browser A migrated and the user
  // edited prices on the server; Browser B opens with no local flag → must trust server fact.
  try {
    const overridesRes = await fetch('/api/catalog/overrides', {
      signal: AbortSignal.timeout(10_000),
    });
    if (!overridesRes.ok) {
      // Non-ok (401 expired, 500, etc.) → safe-default: do not migrate against unknown state.
      return;
    }
    // WR-01: validate that `overrides` is actually an array before checking .length.
    // A truthy non-array (e.g. a string) via the old `as` cast would pass the length check,
    // permanently setting the migration flag and blocking all future guest migrations (DATA LOSS).
    // Minimal schema: LAYER 2 only needs to know IF the server has data — it does not apply
    // the overrides here (that happens in catalogOverridesLoader with full schema validation).
    const arrayGuard = z.object({ overrides: z.array(z.unknown()) });
    const parsed = arrayGuard.safeParse(await overridesRes.json());
    if (!parsed.success) return; // malformed response → safe retry; do not set flag
    if (parsed.data.overrides.length > 0) {
      // Server already has override data → another browser already migrated.
      // Set the local fast-path flag so future logins skip this GET round-trip.
      localStorage.setItem(getCatalogMigratedFlag(userId), 'true');
      return;
    }
    // Overrides empty → check custom services before proceeding (Pitfall 4 partial case).
  } catch {
    // Network error / AbortError → offline safe-default: no writes, no flag.
    return;
  }

  // LAYER 2b: also check custom services — user might have only custom services, no overrides.
  // Failure here is non-critical: swallow and continue (unlike LAYER 2a which gates all writes).
  try {
    const servicesRes = await fetch('/api/catalog/services', {
      signal: AbortSignal.timeout(10_000),
    });
    if (servicesRes.ok) {
      // WR-01: minimal array guard — same rationale as LAYER 2a above.
      const svcArrayGuard = z.object({ services: z.array(z.unknown()) });
      const parsedSvc = svcArrayGuard.safeParse(await servicesRes.json());
      if (parsedSvc.success && parsedSvc.data.services.length > 0) {
        localStorage.setItem(getCatalogMigratedFlag(userId), 'true');
        return;
      }
    }
  } catch {
    // Non-critical: continue with migration even if custom-services check fails.
  }

  // Read local catalog from localStorage (Zustand-persist key 'estimatix-services').
  const rawServicesStore = (() => {
    try {
      return window.localStorage.getItem('estimatix-services');
    } catch {
      return null;
    }
  })();

  // No local catalog data at all → nothing to migrate; mark done.
  if (!rawServicesStore) {
    localStorage.setItem(getCatalogMigratedFlag(userId), 'true');
    return;
  }

  let localServices: Service[];
  try {
    const parsed = JSON.parse(rawServicesStore);
    localServices = (parsed?.state?.rawServices as Service[]) ?? [];
  } catch {
    // Corrupt localStorage → skip silently (do NOT set flag — retry next login may be ok).
    return;
  }

  // Empty catalog → nothing to migrate; mark done.
  if (localServices.length === 0) {
    localStorage.setItem(getCatalogMigratedFlag(userId), 'true');
    return;
  }

  // Build a lookup map for fast diff vs SERVICES_DB bundle defaults.
  const servicesDBMap = new Map<string, Service>(SERVICES_DB.map((s) => [s.id, s as Service]));
  let errors = 0;

  for (const service of localServices) {
    if (service.isCustom) {
      // Custom service → PUT /api/catalog/services/:id (upsert; idempotent).
      try {
        const res = await fetch(`/api/catalog/services/${encodeURIComponent(service.id)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(10_000),
          body: JSON.stringify({
            name: service.name,
            unit: service.unit,
            category_id: service.categoryId ?? null,
            unit_type: service.unitType ?? null,
            price_labor: Math.max(0, parseSafeNumber(service.priceLabor)), // CR-01: clamp NaN/negative
          }),
        });
        if (!res.ok) errors++;
      } catch {
        errors++;
      }
    } else {
      // System service — compute delta vs SERVICES_DB bundle.
      const bundleDefault = servicesDBMap.get(service.id);
      if (!bundleDefault) continue; // Unknown id (removed from bundle) → skip safely.

      const priceChanged = service.priceLabor !== bundleDefault.priceLabor;
      // nameKey present → still using the i18n key (not a manual rename).
      // nameKey absent AND name differs from bundle → user manually renamed this service.
      const nameIsManual = !service.nameKey && service.name !== bundleDefault.name;

      if (!priceChanged && !nameIsManual) continue; // Matches bundle defaults exactly → skip.

      // Build delta body — only include the fields that differ (mirrors syncOverrideToServer).
      const body: Record<string, unknown> = { service_id: service.id };
      if (priceChanged) {
        body.price_labor = Math.max(0, parseSafeNumber(service.priceLabor)); // CR-01
      }
      if (nameIsManual) {
        body.name = service.name;
        body.name_is_manual = true;
      }

      try {
        const res = await fetch('/api/catalog/overrides', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          signal: AbortSignal.timeout(10_000),
          body: JSON.stringify(body),
        });
        if (!res.ok) errors++;
      } catch {
        errors++;
      }
    }
  }

  // Set the per-userId catalog-migrated flag ONLY when ALL uploads succeeded (Pitfall 3 / T-13-05, HARDEN-01).
  // Partial failure leaves the flag unset → next login retries with idempotent upserts.
  if (errors === 0) {
    localStorage.setItem(getCatalogMigratedFlag(userId), 'true');
  }
  // If errors > 0: flag unset → next login retries. Server upserts ensure no duplication.
}